import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Eye,
  Newspaper,
  Search,
  Tag,
  X,
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';
import PageHero from '../components/common/PageHero';
import { assetUrl } from '../utils/assets';

const PAGE_TITLE = 'Latest News & Updates';
const PAGE_SUBTITLE = 'Stay informed with the latest happenings at Katine TTI';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, [selectedCategory]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let url = '/blog/posts?limit=50&offset=0';
      if (selectedCategory) url += `&category=${selectedCategory}`;

      const [postsRes, categoriesRes] = await Promise.all([
        api.get(url),
        api.get('/blog/categories')
      ]);

      const allPosts = postsRes.data.posts || [];
      setPosts(allPosts);

      setFeaturedPosts(allPosts.slice(0, 5));
      setRecentPosts(allPosts.slice(5, 20));
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  };

  const handleCategoryClick = (slug) => {
    setSelectedCategory(slug === selectedCategory ? '' : slug);
    setCurrentSlide(0);
  };

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % featuredPosts.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + featuredPosts.length) % featuredPosts.length);
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  const getImageUrl = assetUrl;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const truncateText = (text, length = 120) => {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.substring(0, length) + '...';
  };

  /** Editor content is HTML, so strip tags before using it as plain summary text. */
  const plainText = (html) =>
    (html || '')
      .replace(/<[^>]*>/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

  const summaryOf = (post) =>
    (post?.excerpt && post.excerpt.trim()) ||
    truncateText(plainText(post?.content), 150);

  const query = searchTerm.trim().toLowerCase();

  const matchesSearch = (post) => {
    if (!query) return true;
    const haystack = `${post.title || ''} ${summaryOf(post)}`.toLowerCase();
    return haystack.includes(query);
  };

  const activeFeatured = featuredPosts[currentSlide] || null;

  const gridPosts = posts.filter(
    (post) => post.slug !== activeFeatured?.slug && matchesSearch(post)
  );

  const recentList = (recentPosts.length > 0 ? recentPosts : posts)
    .filter((post) => post.slug !== activeFeatured?.slug)
    .slice(0, 4);

  const countForCategory = (category) =>
    category.post_count !== undefined && category.post_count !== null
      ? Number(category.post_count)
      : posts.filter((post) => post.category_id === category.id).length;

  const isFiltering = Boolean(selectedCategory || query);
  const articleLabel = `${posts.length} ${posts.length === 1 ? 'article' : 'articles'}`;
  const resultLabel = `${gridPosts.length} ${
    gridPosts.length === 1 ? 'result' : 'results'
  } for "${searchTerm.trim()}"`;

  const clearFilters = () => {
    setSearchTerm('');
    if (selectedCategory) handleCategoryClick(selectedCategory);
  };

  if (loading) {
    return (
      <div className="bg-ink-50">
        <PageHero icon={Newspaper} title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />
        <div className="container-page py-16">
          <div className="flex items-center justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-brand-700"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ink-50">
      <PageHero icon={Newspaper} title={PAGE_TITLE} subtitle={PAGE_SUBTITLE} />

      <div className="container-page py-8 sm:py-10">
        {/* Topic filter strip */}
        {categories.length > 0 && (
          <div className="mb-8 flex flex-wrap items-center gap-2">
            <span className="eyebrow">
              <Tag className="h-3 w-3" aria-hidden="true" />
              Topics
            </span>
            <button
              type="button"
              onClick={() => setSelectedCategory('')}
              aria-pressed={!selectedCategory}
              className={`badge transition-colors ${
                !selectedCategory
                  ? 'bg-brand-700 text-white'
                  : 'bg-white text-ink-700 ring-1 ring-ink-200 hover:bg-ink-100'
              }`}
            >
              All
            </button>
            {categories.map((category) => {
              const isActive = selectedCategory === category.slug;
              return (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => handleCategoryClick(category.slug)}
                  aria-pressed={isActive}
                  className={`badge transition-colors ${
                    isActive
                      ? 'bg-brand-700 text-white'
                      : 'bg-white text-ink-700 ring-1 ring-ink-200 hover:bg-ink-100'
                  }`}
                >
                  {category.name}
                </button>
              );
            })}
          </div>
        )}

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-10">
          {/* Main column */}
          <div className="space-y-10 lg:col-span-2">
            {posts.length === 0 ? (
              <div className="card flex flex-col items-center px-6 py-14 text-center">
                <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-ink-100 text-ink-500">
                  <Newspaper className="h-7 w-7" aria-hidden="true" />
                </span>
                <h2 className="text-lg font-bold text-ink-900">No articles yet</h2>
                <p className="mt-2 max-w-sm text-sm leading-relaxed text-ink-500">
                  {isFiltering
                    ? 'Nothing matches the current filters. Try another topic or clear your search.'
                    : 'Published news and updates from Katine TTI will appear here as soon as they go live.'}
                </p>
                {isFiltering && (
                  <button type="button" onClick={clearFilters} className="btn-outline mt-6">
                    <X className="h-4 w-4" aria-hidden="true" />
                    Clear filters
                  </button>
                )}
              </div>
            ) : (
              <>
                {activeFeatured && (
                  <section aria-label="Featured article">
                    <div className="mb-4 flex items-center justify-between gap-3">
                      <span className="eyebrow">Featured</span>

                      {featuredPosts.length > 1 && (
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={prevSlide}
                            aria-label="Previous featured article"
                            className="btn-ghost h-11 w-11 min-h-0 rounded-full p-0 text-ink-600"
                          >
                            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                          </button>
                          <div className="flex items-center gap-1 px-1">
                            {featuredPosts.map((post, index) => (
                              <button
                                key={post.id ?? index}
                                type="button"
                                onClick={() => goToSlide(index)}
                                aria-label={`Show featured article ${index + 1}`}
                                aria-current={currentSlide === index ? 'true' : undefined}
                                className="group flex h-6 w-4 items-center justify-center"
                              >
                                <span
                                  className={`block rounded-full transition-all ${
                                    currentSlide === index
                                      ? 'h-1.5 w-4 bg-gold-500'
                                      : 'h-1.5 w-1.5 bg-ink-300 group-hover:bg-ink-400'
                                  }`}
                                />
                              </button>
                            ))}
                          </div>
                          <button
                            type="button"
                            onClick={nextSlide}
                            aria-label="Next featured article"
                            className="btn-ghost h-11 w-11 min-h-0 rounded-full p-0 text-ink-600"
                          >
                            <ChevronRight className="h-4 w-4" aria-hidden="true" />
                          </button>
                        </div>
                      )}
                    </div>

                    <article className="card overflow-hidden p-0">
                      <div className="grid md:grid-cols-2">
                        <div className="relative aspect-[16/10] overflow-hidden bg-brand-800 md:aspect-auto md:min-h-[18rem]">
                          <div className="absolute inset-0 flex items-center justify-center bg-brand-800">
                            <Newspaper className="h-10 w-10 text-white/40" aria-hidden="true" />
                          </div>
                          {getImageUrl(activeFeatured.featured_image) && (
                            <img
                              src={getImageUrl(activeFeatured.featured_image)}
                              alt={activeFeatured.title}
                              className="relative h-full w-full object-cover"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                        </div>

                        <div className="flex flex-col justify-center p-5 sm:p-6 lg:p-8">
                          {activeFeatured.category_name && (
                            <span className="badge-gold mb-3 self-start">
                              {activeFeatured.category_name}
                            </span>
                          )}
                          <h2 className="text-xl font-bold leading-snug text-ink-900 sm:text-2xl">
                            <Link
                              to={`/blog/${activeFeatured.slug}`}
                              className="transition-colors hover:text-brand-700"
                            >
                              {activeFeatured.title}
                            </Link>
                          </h2>
                          <p className="mt-3 line-clamp-3 text-sm leading-relaxed text-ink-600 sm:text-base">
                            {summaryOf(activeFeatured)}
                          </p>
                          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-ink-500">
                            <span className="inline-flex items-center gap-1.5">
                              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                              {formatDate(activeFeatured.published_at)}
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                              <Eye className="h-3.5 w-3.5" aria-hidden="true" />
                              {activeFeatured.view_count || 0} views
                            </span>
                          </div>
                          <div className="mt-6">
                            <Link to={`/blog/${activeFeatured.slug}`} className="btn-primary">
                              Read article
                              <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </article>
                  </section>
                )}

                <section aria-labelledby="blog-latest-heading">
                  <div className="mb-5 flex flex-wrap items-end justify-between gap-2">
                    <div>
                      <h2 id="blog-latest-heading" className="section-title text-xl sm:text-2xl">
                        {query ? 'Search results' : 'More stories'}
                      </h2>
                      <p className="mt-1 text-sm text-ink-500">
                        {query ? resultLabel : articleLabel}
                      </p>
                    </div>
                    {query && (
                      <button
                        type="button"
                        onClick={() => setSearchTerm('')}
                        className="inline-flex items-center gap-1 text-sm font-semibold text-brand-700 transition-colors hover:text-gold-700"
                      >
                        <X className="h-3.5 w-3.5" aria-hidden="true" />
                        Clear search
                      </button>
                    )}
                  </div>

                  {gridPosts.length === 0 ? (
                    query ? (
                      <div className="card flex flex-col items-center px-6 py-12 text-center">
                        <span className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-ink-100 text-ink-500">
                          <Search className="h-6 w-6" aria-hidden="true" />
                        </span>
                        <p className="text-sm font-semibold text-ink-900">
                          No matching articles
                        </p>
                        <p className="mt-1 max-w-sm text-sm text-ink-500">
                          Try a different keyword, or browse everything again.
                        </p>
                        <button type="button" onClick={clearFilters} className="btn-outline mt-5">
                          Clear filters
                        </button>
                      </div>
                    ) : (
                      <p className="rounded-xl border border-dashed border-ink-300 bg-white/60 px-6 py-8 text-center text-sm text-ink-500">
                        You are all caught up — there are no further articles in this view.
                      </p>
                    )
                  ) : (
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                      {gridPosts.map((post) => (
                        <Link
                          key={post.id}
                          to={`/blog/${post.slug}`}
                          className="card-interactive group flex flex-col overflow-hidden p-0"
                        >
                          <div className="relative aspect-[16/10] overflow-hidden bg-brand-800">
                            <div className="absolute inset-0 flex items-center justify-center bg-brand-800">
                              <Newspaper className="h-8 w-8 text-white/40" aria-hidden="true" />
                            </div>
                            {getImageUrl(post.featured_image) && (
                              <img
                                src={getImageUrl(post.featured_image)}
                                alt={post.title}
                                className="relative h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            )}
                            {post.category_name && (
                              <span className="badge absolute left-3 top-3 bg-gold-400 text-brand-950">
                                {post.category_name}
                              </span>
                            )}
                          </div>

                          <div className="flex flex-1 flex-col p-5">
                            <span className="mb-2 inline-flex items-center gap-1.5 text-xs text-ink-500">
                              <CalendarDays className="h-3.5 w-3.5" aria-hidden="true" />
                              {formatDate(post.published_at)}
                            </span>
                            <h3 className="line-clamp-2 text-base font-bold leading-snug text-ink-900 transition-colors group-hover:text-brand-700">
                              {post.title}
                            </h3>
                            <p className="mt-2 line-clamp-3 flex-1 text-sm leading-relaxed text-ink-600">
                              {summaryOf(post)}
                            </p>
                            <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-brand-700 transition-colors group-hover:text-gold-700">
                              Read more
                              <ArrowRight className="h-4 w-4" aria-hidden="true" />
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </section>
              </>
            )}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="space-y-6 lg:sticky lg:top-24">
              {/* Search */}
              <div className="card p-5">
                <label htmlFor="blog-search" className="label">
                  Search articles
                </label>
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400"
                    aria-hidden="true"
                  />
                  <input
                    id="blog-search"
                    type="search"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Title or summary"
                    className="input-field pl-9 pr-9 [&::-webkit-search-cancel-button]:appearance-none"
                  />
                  {searchTerm && (
                    <button
                      type="button"
                      onClick={() => setSearchTerm('')}
                      aria-label="Clear search"
                      className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
                    >
                      <X className="h-4 w-4" aria-hidden="true" />
                    </button>
                  )}
                </div>
              </div>

              {/* Categories */}
              <div className="card p-5">
                <div className="mb-3 flex items-center justify-between border-b border-ink-200 pb-2">
                  <h2 className="text-sm font-bold text-ink-900">Categories</h2>
                  <Tag className="h-4 w-4 text-ink-400" aria-hidden="true" />
                </div>

                {categories.length === 0 ? (
                  <p className="text-sm text-ink-500">No categories yet.</p>
                ) : (
                  <ul className="space-y-1">
                    <li>
                      <button
                        type="button"
                        onClick={() => setSelectedCategory('')}
                        aria-pressed={!selectedCategory}
                        className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                          !selectedCategory
                            ? 'bg-brand-50 font-semibold text-brand-800 ring-1 ring-brand-100'
                            : 'text-ink-700 hover:bg-ink-100'
                        }`}
                      >
                        All topics
                        <span className="text-xs tabular-nums text-ink-500">{posts.length}</span>
                      </button>
                    </li>
                    {categories.map((category) => {
                      const isActive = selectedCategory === category.slug;
                      const count = countForCategory(category);
                      return (
                        <li key={category.id}>
                          <button
                            type="button"
                            onClick={() => handleCategoryClick(category.slug)}
                            aria-pressed={isActive}
                            className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                              isActive
                                ? 'bg-brand-50 font-semibold text-brand-800 ring-1 ring-brand-100'
                                : 'text-ink-700 hover:bg-ink-100'
                            }`}
                          >
                            <span className="truncate">{category.name}</span>
                            <span className="flex-shrink-0 text-xs tabular-nums text-ink-500">
                              {count}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {/* Recent posts */}
              <div className="card p-5">
                <div className="mb-3 flex items-center justify-between border-b border-ink-200 pb-2">
                  <h2 className="text-sm font-bold text-ink-900">Recent posts</h2>
                  <Newspaper className="h-4 w-4 text-ink-400" aria-hidden="true" />
                </div>

                {recentList.length === 0 ? (
                  <p className="text-sm text-ink-500">No other posts yet.</p>
                ) : (
                  <ul className="space-y-3">
                    {recentList.map((post) => (
                      <li key={post.id}>
                        <Link to={`/blog/${post.slug}`} className="group flex gap-3">
                          <div className="relative h-14 w-14 flex-shrink-0 overflow-hidden rounded-lg bg-brand-800">
                            <div className="absolute inset-0 flex items-center justify-center bg-brand-800">
                              <Newspaper className="h-5 w-5 text-white/40" aria-hidden="true" />
                            </div>
                            {getImageUrl(post.featured_image) && (
                              <img
                                src={getImageUrl(post.featured_image)}
                                alt={post.title}
                                className="relative h-full w-full object-cover"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                }}
                              />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="line-clamp-2 text-sm font-semibold leading-snug text-ink-800 transition-colors group-hover:text-brand-700">
                              {post.title}
                            </p>
                            <p className="mt-1 text-xs text-ink-500">
                              {formatDate(post.published_at)}
                            </p>
                          </div>
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;
